<template>
  <div>
    <!-- Main Search Filters Row -->
    <div class="grid grid-cols-1 xl:grid-cols-5 gap-4 mb-8">
      <!-- Vehicle Types Filter - Hydrate on interaction -->
      <div>
        <div class="text-xl font-bold text-primary mb-2 hidden xl:block">
          {{ filteredConditions.length }} Vehicle Types
        </div>
        <ClientOnly>
          <button 
            @click="openDialog('condition')" 
            class="w-full p-4 bg-card text-foreground rounded-lg flex justify-between items-center border border-border border-4"
          >
            <span>{{ getActiveFilterText('condition', `All ${filteredConditions.length} Types`) }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          
          <!-- Vehicle Types Filter Dialog - Hydrate on demand -->
          <LazyInteractionDialog v-model:open="dialogs.condition" hydrate-on-interaction="click">
            <DialogContent class="sm:max-w-md md:rounded-lg sm:h-auto sm:max-h-[34vh] max-h-[85vh] w-screen max-w-full sm:max-w-md flex flex-col p-0 sm:p-6 overflow-hidden">
              <!-- Mobile Header with Close button only -->
              <div class="flex justify-end items-center p-3 md:hidden absolute top-0 right-0 z-10">
                <DialogClose as-child>
                  <Button variant="ghost" class="p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-6 w-6"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                  </Button>
                </DialogClose>
              </div>
              
              <!-- Desktop Header -->
              <DialogHeader class="hidden md:block pt-0 px-0">
                <DialogTitle>Select Vehicle Types</DialogTitle>
                <DialogDescription>Choose new, used, or demo vehicles.</DialogDescription>
              </DialogHeader>

              <!-- Desktop Tab Navigation -->
              <div class="hidden md:flex border-b border-border">
                <button
                  v-for="tab in desktopTabs"
                  :key="tab.id"
                  @click="navigateToTab(tab.id, 'condition')"
                  :class="[
                    'relative py-3 px-4 -mb-px border-b-2 font-medium text-sm focus:outline-none',
                    (dialogs.condition && tab.id === 'condition') 
                      ? 'border-primary text-primary' 
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  ]"
                >
                  {{ tab.label }}
                  <span 
                    v-if="getSelectedCountForTab(tab.id) > 0"
                    class="absolute top-0.5 right-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground transform translate-x-1/4 -translate-y-1/4"
                  >
                    {{ getSelectedCountForTab(tab.id) }}
                  </span>
                </button>
              </div>

              <!-- Mobile Title and Description -->
              <div class="md:hidden p-6">
                <DialogTitle class="text-2xl font-semibold mb-1">Available Vehicle Types</DialogTitle>
                <DialogDescription>Explore our great range of new, demonstrator, and pre-owned vehicles across our network of dealerships.</DialogDescription>
              </div>
              
              <div class="overflow-y-auto px-6 sm:px-0 pb-20 md:pb-0 md:flex-1">
              <!-- START: Display selected conditions as pills/chips -->
              <div v-if="selectedConditionsForDialog.length > 0" class="flex flex-wrap gap-2 mb-4">
                  <div 
                    v-for="item in selectedConditionsForDialog" 
                    :key="item.value" 
                    class="px-3 py-1.5 bg-black/5 rounded-full flex items-center text-sm"
                  >
                    <span>{{ item.displayValue }}</span>
                    <Button
                      @click="removeFilterItem('condition', item.value)"
                      variant="ghost"
                      size="sm"
                      class="ml-1 p-0 h-auto hover:bg-transparent"
                      :aria-label="`Remove filter ${item.displayValue}`"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                    </Button>
                  </div>
              </div>
              <!-- END: Display selected conditions as pills/chips -->
              <div class="space-y-2 mb-6">
                <div 
                  v-for="option in filteredConditions.filter((o: FilterOption) => (o.count ?? 0) > 0)" 
                 
                  :class="[
                    'flex items-center p-3 cursor-pointer rounded-lg text-foreground -mx-3 sm:mx-0',
                    isFilterOptionSelected('condition', option.value) ? 'bg-black/10' : 'hover:bg-black/5'
                  ]"
                
                >
                <Checkbox
                      :id="`desktop-condition-${option.value}`"
                      :model-value="isFilterOptionSelected('condition', option.value)"
                      @update:model-value="toggleFilterOption('condition', option.value)"
                      class="mr-2"
                    />
                    <label 
                      :for="`desktop-condition-${option.value}`" 
                      class="flex-1 cursor-pointer font-bold text-lg"
                    >
                      {{ option.displayValue }}
                    </label>
                  <span class="ml-auto text-muted-foreground font-medium">{{ option.count ?? 0 }}</span>
                </div>
              </div>
              </div>

              <!-- Desktop Footer -->
              <DialogFooter class="hidden md:flex pt-4 border-t border-border sm:border-t-0">
                <Button @click="clearFilter('condition'); closeDialog('condition')" variant="outline">
                  Clear Selection
                </Button>
                <Button @click="closeDialog('condition')">
                  Done
                </Button>
              </DialogFooter>

              <!-- Mobile Footer Navigation -->
              <div class="fixed sm:max-h-300 bottom-0 left-0 right-0 bg-background p-4 flex gap-3 z-10">
                <Button variant="outline" @click="closeDialog('condition')" class="flex-1 py-3 h-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  Close
                </Button>
                <Button @click="openDialog('make'); closeDialog('condition')" class="flex-1 py-3 h-auto bg-primary">
                  Make
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </Button>
              </div>
            </DialogContent>
          </LazyInteractionDialog>
        </ClientOnly>
      </div>
      
      <!-- Makes Filter - Hydrate on interaction -->
      <div>
        <div class="text-xl font-bold text-primary mb-2 hidden xl:block">
          {{ filteredMakes.length }} Makes
        </div>
        <ClientOnly>
          <button 
            @click="openDialog('make')" 
            class="w-full p-4 bg-card text-foreground rounded-lg flex justify-between items-center border border-border border-4"
          >
            <span>{{ getActiveFilterText('make', `All ${filteredMakes.length} Makes`) }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          
          <!-- Makes Filter Dialog - Hydrate on demand -->
          <LazyInteractionDialog v-model:open="dialogs.make" hydrate-on-interaction="click">
            <DialogContent class="sm:max-w-md md:rounded-lg sm:h-auto sm:max-h-[70vh] max-h-[85vh] w-screen max-w-full sm:max-w-md flex flex-col p-0 sm:p-6 overflow-hidden">
              <!-- Mobile Header with Close button only -->
              <div class="flex justify-end items-center p-3 md:hidden absolute top-0 right-0 z-10">
                <DialogClose as-child>
                  <Button variant="ghost" class="p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-6 w-6"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                  </Button>
                </DialogClose>
              </div>
              
              <!-- Desktop Header -->
              <DialogHeader class="hidden md:block pt-0 px-0">
                <DialogTitle>Select Makes</DialogTitle>
                <DialogDescription>Choose one or more vehicle makes below.</DialogDescription>
              </DialogHeader>

              <!-- Desktop Tab Navigation -->
              <div class="hidden md:flex border-b border-border">
                <button
                  v-for="tab in desktopTabs"
                  :key="tab.id"
                  @click="navigateToTab(tab.id, 'make')"
                  :class="[
                    'relative py-3 px-4 -mb-px border-b-2 font-medium text-sm focus:outline-none',
                    (dialogs.make && tab.id === 'make') 
                      ? 'border-primary text-primary' 
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  ]"
                >
                  {{ tab.label }}
                  <span 
                    v-if="getSelectedCountForTab(tab.id) > 0"
                    class="absolute top-0.5 right-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground transform translate-x-1/4 -translate-y-1/4"
                  >
                    {{ getSelectedCountForTab(tab.id) }}
                  </span>
                </button>
              </div>

              <!-- Mobile Title and Description -->
              <div class="md:hidden p-6">
                <DialogTitle class="text-2xl font-semibold mb-1">Available Makes</DialogTitle>
                <DialogDescription>Choose from our range of available vehicle makes.</DialogDescription>
              </div>
              <div class="overflow-y-auto px-6 sm:px-0 pb-20 md:pb-0 md:flex-1">
              <!-- START: Display selected makes as pills/chips -->
              <div v-if="selectedMakesForDialog.length > 0" class="flex flex-wrap gap-2 mb-4">
                  <div 
                    v-for="item in selectedMakesForDialog" 
                    :key="item.value" 
                    class="px-3 py-1.5 bg-black/5 rounded-full flex items-center text-sm"
                  >
                    <span>{{ item.displayValue }}</span>
                    <Button
                      @click="removeFilterItem('make', item.value)"
                      variant="ghost"
                      size="sm"
                      class="ml-1 p-0 h-auto hover:bg-transparent"
                      :aria-label="`Remove filter ${item.displayValue}`"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                    </Button>
                  </div>
              </div>
              <!-- END: Display selected makes as pills/chips -->
              <div class="space-y-2 mb-6">
                <div 
                  v-for="option in filteredMakes.filter((o: FilterOption) => (o.count ?? 0) > 0)" 
                  :key="option.value"
                  :class="[
                    'flex items-center p-3 cursor-pointer rounded-lg text-foreground -mx-3 sm:mx-0',
                    isFilterOptionSelected('make', option.value) ? 'bg-black/10' : 'hover:bg-black/5'
                  ]"
                  @click="toggleFilterOption('make', option.value)"
                >
                  <img 
                    v-if="option.logoUrl" 
                    :src="option.logoUrl" 
                    :alt="option.displayValue" 
                    class="h-8 w-8 mr-3 object-contain"
                  />
                  <Checkbox
                      :id="`desktop-make-${option.value}`"
                      :model-value="isFilterOptionSelected('make', option.value)"
                      @update:model-value="toggleFilterOption('make', option.value)"
                      class="mr-2"
                    />
                    <label 
                      :for="`desktop-make-${option.value}`" 
                      class="flex-1 cursor-pointer font-bold text-lg"
                    >
                      {{ option.displayValue }}
                    </label>
                  <span class="ml-auto text-muted-foreground font-medium">{{ option.count ?? 0 }}</span>
                </div>
              </div>
              </div>

              <!-- Desktop Footer -->
              <DialogFooter class="hidden md:flex pt-4 border-t border-border sm:border-t-0">
                <Button @click="clearFilter('make'); closeDialog('make')" variant="outline">Clear Selection</Button>
                <Button @click="closeDialog('make')">Done</Button>
              </DialogFooter>

              <!-- Mobile Footer Navigation -->
              <div class="fixed bottom-0 left-0 right-0 bg-background p-4 flex gap-3 z-10">
                <Button variant="outline" @click="openDialog('condition'); closeDialog('make')" class="flex-1 py-3 h-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                  Condition
                </Button>
                <Button variant="outline" @click="closeDialog('make')" class="w-auto px-4 py-3 h-auto">
                   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </Button>
                <Button @click="openDialog('model'); closeDialog('make')" class="flex-1 py-3 h-auto bg-primary">
                  Model
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </Button>
              </div>
            </DialogContent>
          </LazyInteractionDialog>
        </ClientOnly>
      </div>
      
      <!-- Models Filter - Hydrate on interaction -->
      <div>
        <div class="text-xl font-bold text-primary mb-2 hidden xl:block">
          {{ filteredModels.length }} Models
        </div>
        <ClientOnly>
          <button 
            @click="openDialog('model')" 
            class="w-full p-4 bg-card text-foreground rounded-lg flex justify-between items-center border border-border border-4"
          >
            <span>{{ getActiveFilterText('model', `All ${filteredModels.length} Models`) }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          
          <!-- Models Filter Dialog - Hydrate on demand -->
          <LazyInteractionDialog v-model:open="dialogs.model" hydrate-on-interaction="click">
            <DialogContent class="sm:max-w-md md:rounded-lg sm:h-auto sm:max-h-[70vh] max-h-[85vh] w-screen max-w-full sm:max-w-md flex flex-col p-0 sm:p-6 overflow-hidden">
              <!-- Mobile Header with Close button only -->
              <div class="flex justify-end items-center p-3 md:hidden absolute top-0 right-0 z-10">
                <DialogClose as-child>
                  <Button variant="ghost" class="p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-6 w-6"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                  </Button>
                </DialogClose>
              </div>
              
              <!-- Desktop Header -->
              <DialogHeader class="hidden md:block pt-0 px-0">
                <DialogTitle>Select Models</DialogTitle>
                <DialogDescription>Choose one or more vehicle models below.</DialogDescription>
              </DialogHeader>
              
              <!-- Desktop Tab Navigation -->
              <div class="hidden md:flex border-b border-border">
                <button
                  v-for="tab in desktopTabs"
                  :key="tab.id"
                  @click="navigateToTab(tab.id, 'model')"
                  :class="[
                    'relative py-3 px-4 -mb-px border-b-2 font-medium text-sm focus:outline-none',
                    (dialogs.model && tab.id === 'model') 
                      ? 'border-primary text-primary' 
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  ]"
                >
                  {{ tab.label }}
                  <span 
                    v-if="getSelectedCountForTab(tab.id) > 0"
                    class="absolute top-0.5 right-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground transform translate-x-1/4 -translate-y-1/4"
                  >
                    {{ getSelectedCountForTab(tab.id) }}
                  </span>
                </button>
              </div>

              <!-- Mobile Title and Description -->
              <div class="md:hidden p-6">
                <DialogTitle class="text-2xl font-semibold mb-1">Available Models</DialogTitle>
                <DialogDescription>Choose from our selection of vehicle models.</DialogDescription>
              </div>
              <div class="overflow-y-auto px-6 sm:px-0 pb-20 md:pb-0 md:flex-1">
              
              <!-- START: Display selected models as pills/chips -->
              <div v-if="selectedModelsForDialog.length > 0" class="flex flex-wrap gap-2 mb-4">
                  <div 
                    v-for="item in selectedModelsForDialog" 
                    :key="item.value" 
                    class="px-3 py-1.5 bg-black/5 rounded-full flex items-center text-sm"
                  >
                    <span>{{ item.displayMake }} {{ item.displayValue }}</span>
                    <Button
                      @click="removeFilterItem('model', item.value)"
                      variant="ghost"
                      size="sm"
                      class="ml-1 p-0 h-auto hover:bg-transparent"
                      :aria-label="`Remove filter ${item.displayMake} ${item.displayValue}`"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                    </Button>
                  </div>
              </div>
              <!-- END: Display selected models as pills/chips -->

              <!-- Search input for models -->
              <div class="relative mb-6">
                <input 
                  type="text"
                  v-model="modelSearchQuery"
                  placeholder="Search models..."
                  class="w-full px-4 py-3 pr-12 text-base rounded-md border border-input bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary"
                  ref="modelSearchInput"
                  @click="onModelSearchClick"
                />
                <div class="absolute inset-y-0 right-0 flex items-center">
                  <button 
                    v-if="modelSearchQuery"
                    @click="clearModelSearch"
                    class="px-2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear model search"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </button>
                  <button 
                    class="px-2 text-muted-foreground hover:text-foreground"
                    aria-label="Search models"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div class="overflow-auto">
                <!-- Group models by make -->
                <template v-if="groupedModels && Object.keys(groupedModels).length > 0">
                  <div v-for="(models, make) in groupedModels" :key="make" class="mb-2">
                    <!-- Make Header -->
                    <div class="flex items-center p-2 mb-2 text-lg cursor-pointer rounded-lg text-foreground hover:bg-muted">
                      {{ make }}
                    </div>
                    
                    <!-- Models for this make -->
                    <div 
                      v-for="option in models.filter((o: FilterOption) => (o.count ?? 0) > 0)" 
                      :key="option.value"
                      :class="[
                        'flex items-center p-2 mb-2 cursor-pointer rounded-lg text-foreground',
                        isFilterOptionSelected('model', option.value) ? 'bg-black/10' : 'hover:bg-muted'
                      ]"
                    >
                      <Checkbox
                        :id="`desktop-model-${option.value}`"
                        :model-value="isFilterOptionSelected('model', option.value)"
                        @update:model-value="toggleFilterOption('model', option.value)"
                        class="mr-2"
                      />
                      <label 
                        :for="`desktop-model-${option.value}`" 
                        class="flex-1 cursor-pointer font-bold text-lg"
                      >
                        {{ option.displayValue }}
                      </label>
                      <span class="ml-auto mr-2 text-muted-foreground text-sm">{{ getBodyTypeForModel(option.value) }} ({{ option.count ?? 0 }})</span>
                    </div>
                  </div>
                </template>
                
                <!-- Fallback to flat list if no grouping is available -->
                <template v-else>
                  <div 
                    v-for="option in filteredModels.filter((o: FilterOption) => (o.count ?? 0) > 0)" 
                    :key="option.value"
                    :class="[
                      'flex items-center p-2 cursor-pointer rounded text-foreground',
                      isFilterOptionSelected('model', option.value) ? 'bg-black/10' : 'hover:bg-muted'
                    ]"
                  >
                    <Checkbox
                      :id="`desktop-model-${option.value}`"
                      :model-value="isFilterOptionSelected('model', option.value)"
                      @update:model-value="toggleFilterOption('model', option.value)"
                      class="mr-2"
                    />
                    <label 
                      :for="`desktop-model-${option.value}`" 
                      class="flex-1 cursor-pointer"
                    >
                      {{ option.displayValue }}
                    </label>
                    <span class="ml-auto text-muted-foreground text-sm">{{ getBodyTypeForModel(option.value) }} ({{ option.count ?? 0 }})</span>
                  </div>
                </template>
              </div>
              </div> 
              
              <!-- Desktop Footer -->
              <DialogFooter class="hidden md:flex">
                <Button @click="clearFilter('model'); closeDialog('model')" variant="outline">Clear Selection</Button>
                <Button @click="closeDialog('model')">Done</Button>
              </DialogFooter>

              <!-- Mobile Footer Navigation -->
              <div class="fixed bottom-0 left-0 right-0 backdrop-blur-lg sm:bg-background p-4 flex gap-3 z-10">
                <Button variant="outline" @click="openDialog('make'); closeDialog('model')" class="flex-1 py-3 h-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                  Make
                </Button>
                <Button variant="outline" @click="closeDialog('model')" class="w-auto px-4 py-3 h-auto">
                   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </Button>
                <NuxtLink 
                  :to="{ path: '/cars-for-sale', query: currentFilterQuery }" 
                  class="flex-2 py-3 px-10 h-auto bg-primary text-primary-foreground rounded-md flex items-center justify-center"
                  @click="closeDialog('model')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor" > <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" /> </svg>
                  Show {{ filteredVehicleCount }} cars
                </NuxtLink>
              </div>
            </DialogContent>
          </LazyInteractionDialog>
        </ClientOnly>
      </div>
      
      <!-- Keyword Search - Hydrate when visible -->
      <div>
        <div class="text-xl font-bold text-primary mb-2 hidden xl:block">Keyword</div>
        <LazyVisibleSearchComponent @search="onSearch" hydrate-on-visible />
      </div>
      
      <!-- Discover Button - Hydrate when visible -->
      <div>
        <div class="text-xl font-bold text-primary mb-2 hidden xl:block">Discover</div>
        <LazyVisibleNuxtLink 
          :to="{ path: '/cars-for-sale', query: currentFilterQuery }" 
          class="bg-primary rounded-lg overflow-hidden flex items-center justify-center text-primary-foreground text-2xl sm:text-xl font-bold p-4 h-[63px]"
          hydrate-on-visible
        >
          <span class="mr-2">»</span> Show {{ filteredVehicleCount }} cars
        </LazyVisibleNuxtLink>
      </div>
    </div>
    
    <!-- Body Types and Budget Row - Hydrate when visible -->
    <div class="mb-8">
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <!-- Body Types Filter -->
        <div class="w-full lg:w-1/4">
          <ClientOnly>
            <button 
              @click="openDialog('body')" 
              class="w-full p-4 bg-card text-foreground border border-border border-4 rounded-lg flex justify-between items-center"
            >
              <span class="font-medium">{{ getActiveFilterText('body', `All ${filteredBodyTypes.length} body types`) }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
            
            <!-- Body Types Filter Dialog -->
            <Dialog v-model:open="dialogs.body">
              <DialogContent class="sm:max-w-md md:rounded-lg sm:h-auto sm:max-h-[70vh] max-h-[85vh] w-screen max-w-full sm:max-w-md flex flex-col p-0 sm:p-6 overflow-hidden">
                <!-- Mobile Header with Close button only -->
                <div class="flex justify-end items-center p-3 md:hidden absolute top-0 right-0 z-10">
                  <DialogClose as-child>
                    <Button variant="ghost" class="p-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-6 w-6"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                    </Button>
                  </DialogClose>
                </div>

                <!-- Desktop Header -->
                <DialogHeader class="hidden md:block pt-0 px-0">
                  <DialogTitle>Select Body Types</DialogTitle>
                  <DialogDescription>Choose one or more vehicle body types below.</DialogDescription>
                </DialogHeader>

                <!-- Desktop Tab Navigation -->
                <div class="hidden md:flex border-b border-border">
                  <button
                    v-for="tab in desktopTabs"
                    :key="tab.id"
                    @click="navigateToTab(tab.id, 'body')"
                    :class="[
                      'relative py-3 px-4 -mb-px border-b-2 font-medium text-sm focus:outline-none',
                      (dialogs.body && tab.id === 'body') 
                        ? 'border-primary text-primary' 
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                    ]"
                  >
                    {{ tab.label }}
                    <span 
                      v-if="getSelectedCountForTab(tab.id) > 0"
                      class="absolute top-0.5 right-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground transform translate-x-1/4 -translate-y-1/4"
                    >
                      {{ getSelectedCountForTab(tab.id) }}
                    </span>
                  </button>
                </div>

              <!-- Mobile Title and Description -->
              <div class="md:hidden p-6">
                <DialogTitle class="text-2xl font-semibold mb-1">Available Body Types</DialogTitle>
                <DialogDescription>Choose from our range of available body types.</DialogDescription>
              </div>
                <div class="overflow-y-auto px-6 sm:px-0 pb-20 md:pb-0 md:flex-1">
                <!-- START: Display selected body types as pills/chips -->
                <div v-if="selectedBodyTypesForDialog.length > 0" class="flex flex-wrap gap-2 mb-4">
                    <div 
                      v-for="item in selectedBodyTypesForDialog" 
                      :key="item.value" 
                      class="px-3 py-1.5 bg-black/5 rounded-full flex items-center text-sm"
                    >
                      <span>{{ item.displayValue }}</span>
                      <Button
                        @click="removeFilterItem('body', item.value)"
                        variant="ghost"
                        size="sm"
                        class="ml-1 p-0 h-auto hover:bg-transparent"
                        :aria-label="`Remove filter ${item.displayValue}`"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                      </Button>
                    </div>
                </div>
                <!-- END: Display selected body types as pills/chips -->
                <div class="space-y-2 mb-6">
                  <div 
                    v-for="option in filteredBodyTypes.filter((o: FilterOption) => (o.count ?? 0) > 0)" 
                    :key="option.value"
                    :class="[
                      'flex items-center p-3 cursor-pointer rounded-lg text-foreground -mx-3 sm:mx-0',
                      isFilterOptionSelected('body', option.value) ? 'bg-black/10' : 'hover:bg-black/5'
                    ]"
                    @click="toggleFilterOption('body', option.value)"
                  >
                  <Checkbox
                        :id="`desktop-body-${option.value}`"
                        :model-value="isFilterOptionSelected('body', option.value)"
                        @update:model-value="toggleFilterOption('body', option.value)"
                        class="mr-2"
                      />
                      <label 
                        :for="`desktop-body-${option.value}`" 
                        class="flex-1 cursor-pointer font-bold text-lg"
                      >
                        {{ option.displayValue }}
                      </label>

                    <span class="ml-auto text-muted-foreground font-medium">{{ option.count ?? 0 }}</span>
                  </div>
                </div>
                </div>

                <!-- Desktop Footer -->
                <DialogFooter class="hidden md:flex pt-4 border-t border-border sm:border-t-0">
                  <Button @click="clearFilter('body'); closeDialog('body')" variant="outline">Clear Selection</Button>
                  <Button @click="closeDialog('body')">Done</Button>
                </DialogFooter>

                <!-- Mobile Footer Navigation -->
                <div class="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 flex gap-3 z-10">
                  <Button variant="outline" @click="closeDialog('body')" class="flex-1 py-3 h-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    Close
                  </Button>
                          <NuxtLink 
                  :to="{ path: '/cars-for-sale', query: currentFilterQuery }" 
                  class="flex-2 py-3 px-10 h-auto bg-primary text-primary-foreground rounded-md flex items-center justify-center"
                  @click="closeDialog('model')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor" > <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" /> </svg>
                  Show {{ filteredVehicleCount }} cars
                </NuxtLink>
                  <!-- <Button @click="openDialog('model'); closeDialog('body')" class="flex-1 py-3 h-auto bg-primary">
                    Models
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </Button> -->
                </div>
              </DialogContent>
            </Dialog>
          </ClientOnly>
        </div>
        
        <!-- My Weekly Budget -->
        <div class="bg-card rounded-lg shadow-sm sm:shadow-none sm:p-0 p-6 sm:border-none border border-border w-full lg:w-3/4 mx-auto">
  <div class="flex flex-col items-center mb-2">
    
    <div class="text-xl font-bold text-primary text-center">
      <span class="text-grey-600 text-xl font-medium mb-2">My Weekly Budget </span>
      <span class="text-blue-600"> ${{ formatPrice(weeklyBudget[0] || 0) }}</span> to <span class="text-blue-600">${{ formatPrice(weeklyBudget[1] || 0) }}</span>
      <span class="text-sm text-muted-foreground ml-2">per week ({{ filteredVehicleCount }} {{ filteredVehicleCount === 1 ? 'vehicle' : 'vehicles' }})</span>
    </div>
  </div>
  <ClientOnly>
    <template #default>
      <div class="mb-4">
        <!-- Shadcn Slider component -->
        <Slider
          :min="minWeeklyBudget"
          :max="maxWeeklyBudget"
          :model-value="weeklyBudget"
          :step="1"
          @update:model-value="updateBudgetFilter"
          class="mb-2"
        />
      </div>
    </template>
    <template #fallback>
      <div class="h-2 bg-muted rounded-full mb-2"></div>
    </template>
  </ClientOnly>
  <div class="flex justify-between text-sm">
    <span>${{ formatPrice(minWeeklyBudget) }}</span>
    <span>${{ formatPrice(maxWeeklyBudget) }}</span>
  </div>
</div>
      </div>
    </div>
    
    <!-- Mobile Filter Button - Hydrate on interaction -->
    <div class="md:hidden mb-8">
      <ClientOnly>
        <LazyInteractionButton 
          @click="showMobileFilters = true"
          class="w-full"
          variant="default"
          hydrate-on-interaction="mouseover"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clip-rule="evenodd" />
          </svg>
          Filter Vehicles
        </LazyInteractionButton>
        
        <!-- Mobile Filters Dialog - Use component -->
        <MobileFiltersDialog
          v-model:open="showMobileFilters"
          :filtered-conditions="filteredConditions"
          :filtered-body-types="filteredBodyTypes"
          :filtered-makes="filteredMakes"
          :filtered-models="filteredModels"
          :model-search-query="modelSearchQuery"
          :weekly-budget="weeklyBudget"
          :min-weekly-budget="minWeeklyBudget"
          :max-weekly-budget="maxWeeklyBudget"
          :filtered-vehicle-count="filteredVehicleCount"
          :current-filter-query="currentFilterQuery"
          :is-filter-option-selected-func="isFilterOptionSelected"
          @update:model-search-query="modelSearchQuery = $event"
          @toggle-filter-option="toggleFilterOption($event.filterName, $event.optionValue)"
          @clear-model-search="clearModelSearch"
          @update-budget-filter="updateBudgetFilter"
          @clear-all-filters="clearAllFilters"
        />
      </ClientOnly>
    </div>
    
    
    <!-- REVISED Active Filters Section - Hydrate when visible -->
    <div v-if="hasActiveFilters" class="mb-6">
      <h3 class="text-lg font-medium mb-2">Active Filters:</h3>
      <div class="flex flex-wrap gap-2">
        <ClientOnly>
          <template #default>
            <!-- Loop through individual active filter items -->
            <div
              v-for="(item, index) in activeFiltersDisplay"
              :key="`${item.filterName}-${index}`" 
              class="px-3 py-1.5 bg-black/5 rounded-full flex items-center"
            >
              <!-- Display Label: Value -->
              <span>{{ item.displayLabel }}: {{ item.displayValue }}</span>
              <Button
                @click="item.isArrayItem ? removeFilterItem(item.filterName, item.value) : clearFilter(item.filterName)"
                variant="ghost"
                size="sm"
                class="ml-1 p-0 h-auto hover:bg-transparent"
                :aria-label="`Remove filter ${item.displayLabel}: ${item.displayValue}`"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
              </Button>
            </div>

            <!-- Clear All button with consistent styling -->
            <Button
              v-if="activeFiltersDisplay.length > 0"
              @click="clearAllFilters"
              variant="outline"
              size="sm"
              class="px-3 py-1.5 rounded-full"
            >
              Clear All
            </Button>
          </template>
          <template #fallback>
            <!-- Adjusted fallback to show multiple potential badges -->
            <div class="flex flex-wrap gap-2">
              <div v-for="(_, i) in 3" :key="i" class="px-3 py-1 bg-muted text-muted-foreground rounded-full flex items-center h-6 w-24 animate-pulse"></div>
              <div class="px-3 py-1 bg-muted text-muted-foreground rounded-full h-6 w-16 animate-pulse"></div>
            </div>
          </template>
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, type Ref } from 'vue';
import { useFrontPageStore, type FrontPageFilters } from '~/stores/frontPage';
// import { useFilteredOptions } from '~/composables/useFilteredOptions'; // Commented out - missing module
import { useRouter, useRoute } from 'vue-router';
import { useVehiclesStore } from '~/stores/vehicles';
import { useSiteStore } from '~/stores/site';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose
} from '~/layers/base/components/ui/dialog';
import { Button } from '~/layers/base/components/ui/button';
import { Badge } from '~/layers/base/components/ui/badge';
import { Checkbox } from '~/layers/base/components/ui/checkbox';
import { Slider } from '~/layers/base/components/ui/slider';
import SearchComponent from '~/layers/base/components/SearchComponent.vue';
import MobileFiltersDialog from '~/layers/base/components/MobileFiltersDialog.vue';
import type { 
  FilterOption, 
  ArrayFilterData, 
  RangeFilterData, 
  TextFilterData, 
  // ActiveFilters, // Removed if unused
  Vehicle,
  ArrayFilterValue,
  RangeFilterValue,
  ValueDisplayPair
} from '../../../types';

// Initialize stores and router
const vehiclesStore = useVehiclesStore();
const frontPageStore = useFrontPageStore();
const siteStore = useSiteStore();
const router = useRouter();
const route = useRoute();

// Initialize budget range from store directly
const minWeeklyBudget = computed(() => vehiclesStore.availableFilters.perweekRange?.min ?? 0);
const maxWeeklyBudget = computed(() => vehiclesStore.availableFilters.perweekRange?.max ?? 1000);

// UPDATE Composable Usage
const {
  modelSearchQuery,
  filteredConditions,
  filteredMakes,
  filteredModels,
  groupedModels,
  filteredBodyTypes,
  // REMOVE properties not returned by the composable
  // filteredTransmissions, 
  // filteredDrivetrains, 
  // filteredFuelTypes, 
  clearModelSearch,
  isFilterOptionEnabled
} = useFilteredOptions();

// UPDATE Read Functions
const isFilterOptionSelected = (filterName: keyof FrontPageFilters, optionValue: string) => {
  const selected = frontPageStore.frontPageFilters[filterName];
  const isSelected = Array.isArray(selected) && selected.includes(optionValue);
  // Ensure it's checking against a defined array
  return isSelected;
};

const getBodyTypeForModel = (modelValue: string) => {
  // This helper remains the same
  if (!modelValue) return '';
  const vehiclesWithModel = vehiclesStore.vehicles.filter((v: Vehicle) => 
    v.model?.value?.includes(modelValue)
  );
  if (vehiclesWithModel.length === 0) return '';
  const bodyType = vehiclesWithModel[0]?.body?.displayValue?.[0];
  return bodyType || '';
};

const getActiveFilterText = (filterName: keyof FrontPageFilters, defaultText: string): string => {
  const currentFilters = frontPageStore.frontPageFilters;
  const value = currentFilters[filterName];

  if (filterName === 'perweek') {
    const budget = value as { min: number; max: number } | undefined;
    // Check against computed min/max for accurate default check
    if (budget && (budget.min !== minWeeklyBudget.value || budget.max !== maxWeeklyBudget.value)) {
      return `$${budget.min} - $${budget.max}`;
    } else {
      return defaultText;
    }
  } else if (filterName === 'search_keywords') {
    const search = value as string | undefined;
    return search ? search : defaultText;
  } else if (Array.isArray(value) && value.length > 0) {
    // --- UPDATED Model Logic --- 
    if (filterName === 'model') {
        if (value.length === 1) {
            // Find the corresponding model option to get its displayValue
            const modelOption = frontPageStore.dynamicModels.find(opt => opt.value === value[0]);
            return String(modelOption?.displayValue || value[0] || ''); // Fallback to value if not found
        }
        return `${value.length} Models Selected`;
    } 
    // --- Existing logic for other array filters (Make, Body, Condition) --- 
    else {
        // Generic display for other array types (can be enhanced similarly if needed)
        if (value.length === 1) {
           // You could potentially look up display names here too if needed
           return String(value[0]).charAt(0).toUpperCase() + String(value[0]).slice(1); // Simple Capitalization
        }
        // Capitalize filter name for plural default
        const pluralLabel = filterName.charAt(0).toUpperCase() + filterName.slice(1);
        return `${value.length} ${pluralLabel}${value.length > 1 ? 's' : ''} Selected`;
    }
  }
  return defaultText;
};

// *** Re-add formatPrice helper ***
const formatPrice = (value: number) => {
  if (isNaN(value)) return '0'; // Handle potential NaN
  return value.toLocaleString();
};

// UPDATE Interaction Functions
// Only include keys valid for FrontPageFilters
type FrontPageArrayFilterKey = 'condition' | 'make' | 'model' | 'body';

const toggleFilterOption = (filterName: FrontPageArrayFilterKey, optionValue: string) => {
  // Ensure filterName is a valid key before proceeding
  if (!(filterName in frontPageStore.frontPageFilters)) return; 
  
  // Safely get current values, defaulting to an empty array
  const currentValues = [...(frontPageStore.frontPageFilters[filterName] || [])];
  const index = currentValues.indexOf(optionValue);
  if (index > -1) {
    currentValues.splice(index, 1); // Remove
  } else {
    currentValues.push(optionValue); // Add
  }
  frontPageStore.updateFilter(filterName, currentValues); 
};

const clearFilter = (filterName: keyof FrontPageFilters) => {
  frontPageStore.clearFilter(filterName); 
};

const clearAllFilters = () => {
  frontPageStore.clearAllFilters(); 
};

const updateBudgetFilter = (payload: number[] | undefined) => {
  if (payload === undefined || !Array.isArray(payload) || payload.length !== 2) return; 
  const budget = { min: payload[0], max: payload[1] };
  frontPageStore.updateFilter('perweek', budget); 
};

const onSearch = (query: string) => {
  const searchTerm = query.trim();
  frontPageStore.updateFilter('search_keywords', searchTerm); 
};

// *** Add NEW function to remove a single item from an array filter ***
const removeFilterItem = (filterName: keyof FrontPageFilters, itemValue: string) => {
  if (!['make', 'model', 'body', 'condition'].includes(filterName)) return; // Only for array filters

  const currentValues = [...(frontPageStore.frontPageFilters[filterName as 'make' | 'model' | 'body' | 'condition'] || [])];
  const index = currentValues.indexOf(itemValue);

  if (index > -1) {
    const newValues = [...currentValues];
    newValues.splice(index, 1);
    frontPageStore.updateFilter(filterName, newValues);
  }
};

// Define functions needed by template including the new one
defineExpose({
  isFilterOptionSelected,
  getBodyTypeForModel,
  getActiveFilterText,
  toggleFilterOption,
  clearFilter,
  clearAllFilters,
  updateBudgetFilter,
  onSearch,
  clearModelSearch,
  formatPrice,
  removeFilterItem // Expose the new function
});

// Dialog state management
interface DialogState {
  condition: boolean;
  make: boolean;
  model: boolean;
  body: boolean;
  transmission: boolean;
  drivetrain: boolean;
  fuel: boolean;
}

const dialogs = ref<DialogState>({
  condition: false,
  make: false,
  model: false,
  body: false,
  transmission: false,
  drivetrain: false,
  fuel: false
})

// Dialog control functions
const openDialog = (dialogName: keyof DialogState) => {
  dialogs.value[dialogName] = true
}

const closeDialog = (dialogName: keyof DialogState) => {
  dialogs.value[dialogName] = false
}

// Mobile filters dialog state
const showMobileFilters = ref(false)

// Model search input ref and handling
const modelSearchInput = ref<HTMLInputElement | null>(null);

// Handle model search input click
const onModelSearchClick = () => {
  // Focus the input to allow typing
  if (modelSearchInput.value) {
    modelSearchInput.value.focus();
  }
}

// UPDATE Budget slider state
const weeklyBudget = computed({
  get: () => {
    const defaultMin = minWeeklyBudget.value;
    const defaultMax = maxWeeklyBudget.value;
    // Safely read perweek filter (may be optional)
    const filter = frontPageStore.frontPageFilters.perweek;
    const filterMin = filter?.min ?? 0;
    const filterMax = filter?.max ?? 1000;
    // If still at initial filter defaults (0-1000), show the loaded data range
    if (filterMin === 0 && filterMax === 1000) {
      return [defaultMin, defaultMax];
    }
    // Otherwise, reflect the current filter values
    return [filterMin, filterMax];
  },
  set: (value) => updateBudgetFilter(value)
});

// Helper function to convert price to weekly payment (estimated)
const calculateWeeklyPayment = (price: number): number => {
  // This is a simplified estimation - real calculations would include interest rate, term, etc.
  // For demo purposes, assume 5 year loan with 5% interest rate
  const monthlyInterestRate = 0.05 / 12;
  const termMonths = 60;
  const monthlyPayment = price * (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, termMonths)) / 
                        (Math.pow(1 + monthlyInterestRate, termMonths) - 1);
  // Convert monthly to weekly
  return Math.round(monthlyPayment * 12 / 52);
};

// *** UPDATE Computed property for filtered vehicle count ***
const filteredVehicleCount = computed(() => {
  // Read count from the CORRECT store getter
  return frontPageStore.navSearchFilteredCount; // Use the getter with correct perweek logic
});


// --- REVISED Active Filters Display ---
// Returns a flat array of individual active filter items for rendering badges
interface ActiveFilterItem {
  filterName: keyof FrontPageFilters;
  value: any; // The actual value (string for array items, object for range, etc.)
  displayLabel: string;
  displayValue: string;
  isArrayItem: boolean; // True if it's an item from a multi-select filter
}

const activeFiltersDisplay = computed((): ActiveFilterItem[] => {
  const result: ActiveFilterItem[] = [];
  const filters = frontPageStore.frontPageFilters;

  // Helper to get display name, falling back to capitalized value
  const getDisplay = (filterKey: keyof FrontPageFilters, val: string): string => {
      let options: FilterOption[] = [];
      if (filterKey === 'make') options = frontPageStore.dynamicMakes;
      else if (filterKey === 'model') options = frontPageStore.dynamicModels;
      else if (filterKey === 'body') options = frontPageStore.dynamicBodyTypes;
      else if (filterKey === 'condition') options = frontPageStore.dynamicConditions;
      
      const option = options.find(opt => opt.value === val);
      return option?.displayValue || String(val).charAt(0).toUpperCase() + String(val).slice(1); // Fallback
  };

  // Define labels for filters
  const filterLabels: Record<keyof FrontPageFilters, string> = {
      make: 'Make',
      model: 'Model',
      body: 'Body Type',
      condition: 'Condition',
      badge: 'Badge',
      perweek: 'Weekly Budget',
      search_keywords: 'Search'
  };

  for (const key in filters) {
    const filterName = key as keyof FrontPageFilters;
    const value = filters[filterName];
    const displayLabel = filterLabels[filterName] || filterName; // Get defined label or default

    if (filterName === 'perweek') {
      const budgetStoreValue = value as { min: number; max: number } | undefined;

      if (budgetStoreValue) {
        // Placeholder values in the store that often mean "no user selection yet"
        // This is based on the logic in weeklyBudget.get()
        const placeholderMin = 0;
        const placeholderMax = 1000;

        // The actual full range for the slider, derived from vehiclesStore.availableFilters.perweekRange
        const actualFullRangeMin = minWeeklyBudget.value;
        const actualFullRangeMax = maxWeeklyBudget.value;

        const isPlaceholder = budgetStoreValue.min === placeholderMin && budgetStoreValue.max === placeholderMax;
        const isEffectivelyFullRange = budgetStoreValue.min === actualFullRangeMin && budgetStoreValue.max === actualFullRangeMax;
        
        // Display as active filter only if it's not a placeholder AND it's genuinely a narrowed selection
        // (i.e., not just reflecting the full available range).
        if (!isPlaceholder && !isEffectivelyFullRange) {
           result.push({
               filterName,
               value: budgetStoreValue,
               displayLabel,
               displayValue: `$${budgetStoreValue.min} - $${budgetStoreValue.max}`,
               isArrayItem: false
           });
        }
      }
    } else if (filterName === 'search_keywords') {
      const search = value as string | undefined;
      if (search && search.trim()) {
        result.push({
            filterName,
            value: search,
            displayLabel,
            displayValue: search,
            isArrayItem: false
        });
      }
    } else if (Array.isArray(value) && value.length > 0) {
      // Add each selected item as an individual entry
      value.forEach(itemValue => {
          result.push({
              filterName,
              value: itemValue, // Store the actual value for removal
              displayLabel,
              displayValue: getDisplay(filterName, itemValue), // Use helper to get display name
              isArrayItem: true
          });
      });
    }
  }
  return result;
});

// Check if there are any active filters (logic remains similar, check length of the new array)
const hasActiveFilters = computed(() => {
  return activeFiltersDisplay.value.length > 0;
});


// UPDATE URL handling 
const currentFilterQuery = computed(() => {
  return frontPageStore.currentFilterQuery;
});

// Watch for dialog state
watch(() => dialogs.value.model, (isOpen) => {
  if (!isOpen) {
    modelSearchQuery.value = ''
  }
})

// Define isValueDisplayPair locally (if needed by template, else remove)
// function isValueDisplayPair(value: any): value is ValueDisplayPair {
//   return value && typeof value === 'object' && 'value' in value && Array.isArray(value.value);
// }

// --- START: New computed property for selected models in dialog ---
const selectedModelsForDialog = computed(() => {
  const selectedModelValues = frontPageStore.frontPageFilters.model;
  if (!selectedModelValues || selectedModelValues.length === 0) {
    return [];
  }

  return selectedModelValues.map(value => {
    // Find the corresponding model option in the store's dynamic list to get display names
    // This list already contains models filtered by other facets but NOT the search query
    const modelOption = frontPageStore.dynamicModels.find(opt => opt.value === value);

    // Fallbacks if the option isn't found (e.g., if data is slightly out of sync)
    const displayValue = modelOption?.displayValue || value.split('_')[1] || value;
    const displayMake = modelOption?.displayMake || value.split('_')[0];

    return {
      value: value, // Keep the 'make_modelcode' value for removal
      displayValue: displayValue,
      displayMake: displayMake
    };
  }).sort((a, b) => {
      // Sort alphabetically by make then model for consistent display
      const makeCompare = (a.displayMake || '').localeCompare(b.displayMake || '');
      if (makeCompare !== 0) return makeCompare;
      return a.displayValue.localeCompare(b.displayValue);
  });
});
// --- END: New computed property ---

// --- START: New computed properties for other dialogs ---
const selectedConditionsForDialog = computed(() => {
  const selectedValues = frontPageStore.frontPageFilters.condition;
  if (!selectedValues || selectedValues.length === 0) return [];
  return selectedValues.map(value => {
    const option = frontPageStore.dynamicConditions.find(opt => opt.value === value);
    const displayValue = option?.displayValue || String(value).charAt(0).toUpperCase() + String(value).slice(1); // Fallback capitalization
    return { value, displayValue };
  }).sort((a, b) => a.displayValue.localeCompare(b.displayValue));
});

const selectedMakesForDialog = computed(() => {
  const selectedValues = frontPageStore.frontPageFilters.make;
  if (!selectedValues || selectedValues.length === 0) return [];
  return selectedValues.map(value => {
    const option = frontPageStore.dynamicMakes.find(opt => opt.value === value);
    const displayValue = option?.displayValue || String(value).charAt(0).toUpperCase() + String(value).slice(1);
    return { value, displayValue };
  }).sort((a, b) => a.displayValue.localeCompare(b.displayValue));
});

const selectedBodyTypesForDialog = computed(() => {
  const selectedValues = frontPageStore.frontPageFilters.body;
  if (!selectedValues || selectedValues.length === 0) return [];
  return selectedValues.map(value => {
    const option = frontPageStore.dynamicBodyTypes.find(opt => opt.value === value);
    const displayValue = option?.displayValue || String(value).charAt(0).toUpperCase() + String(value).slice(1);
    return { value, displayValue };
  }).sort((a, b) => a.displayValue.localeCompare(b.displayValue));
});
// --- END: New computed properties ---

// --- Desktop Tab Navigation ---
const desktopTabs = [
  { id: 'condition', label: 'Vehicle Types' },
  { id: 'make', label: 'Makes' },
  { id: 'model', label: 'Models' },
  { id: 'body', label: 'Body Types' },
] as const; // Use "as const" for stricter typing of id

type DialogTabId = typeof desktopTabs[number]['id'];

const navigateToTab = (targetTabId: DialogTabId, currentTabId: DialogTabId) => {
  if (targetTabId === currentTabId) return;
  openDialog(targetTabId);
  closeDialog(currentTabId);
};
// --- END Desktop Tab Navigation ---

// --- Helper function for tab counts ---
const getSelectedCountForTab = (tabId: DialogTabId): number => {
  const filters = frontPageStore.frontPageFilters;
  switch (tabId) {
    case 'condition':
      return filters.condition?.length || 0;
    case 'make':
      return filters.make?.length || 0;
    case 'model':
      return filters.model?.length || 0;
    case 'body':
      return filters.body?.length || 0;
    default:
      return 0;
  }
};
// --- END Helper function for tab counts ---
</script>